import get from 'lodash/get';

import { mergeTrackingProperties, transformNamedAnswers } from './calendarCommon';
import { HUBSPOT_URL_REGEX, NAMED_ANSWERS } from './constants';

import type { FormValues, Input } from '$lib/types/Form';

/**
 * HubSpot Meetings integration module
 * Provides utilities for validating URLs, building iframe URLs with prefill parameters,
 * and detecting booking confirmation events
 */
export const hubspot = {
    validateUrl: (url: string): boolean => {
        return HUBSPOT_URL_REGEX.test(url);
    },

    /**
     * Checks if a message event is from HubSpot booking
     */
    isBookingSuccessful: (data: unknown): boolean => {
        if (typeof data !== 'object' || !data) {
            return false;
        }

        const eventData = data as { meetingBookSucceeded?: boolean };
        return eventData.meetingBookSucceeded === true;
    },

    /**
     * Extracts form answers for HubSpot prefill
     * - Named answers (email, name, firstName, lastName, phone) are extracted from form inputs
     * - Tracking properties from previous pages are merged (but not overwritten)
     * - HubSpot supports prefilling: firstname, lastname, email, phone
     */
    extractNamedAnswers: (
        inputs: Input[],
        formData: FormValues,
        allTrackingProperties: FormValues,
    ): FormValues => {
        let counter = 1;
        const namedAnswers: FormValues = {};

        // Extract named answers from current form inputs
        inputs.forEach((input) => {
            const { name } = input;
            const value = get(formData, name, '');

            // Check if field name matches our known fields
            if (NAMED_ANSWERS.includes(name)) {
                namedAnswers[name] = value;
            }

            counter += 1;
        });

        const mergedNamedAnswers = mergeTrackingProperties({
            allTrackingProperties,
            namedAnswers,
            maxTrackingProperties: counter,
        });

        return transformNamedAnswers(mergedNamedAnswers);
    },

    /**
     * Builds the iframe URL with prefill parameters and UTM parameters
     * HubSpot supports these built-in fields: firstname, lastname, email, phone
     * UTM parameters are added to the URL for tracking
     */
    buildIframeUrl: (
        baseUrl: string,
        namedAnswers: FormValues,
        trackingProperties: FormValues,
    ): string => {
        const url = new URL(baseUrl);
        const { name: directName, firstName, lastName, email, phone, company } = namedAnswers;

        // Handle first name with priority:
        // 1. Use firstName if provided directly
        // 2. Use name as firstName if provided
        // 3. Extract first part of name if it contains spaces
        let finalFirstName = '';
        let finalLastName = '';

        if (firstName) {
            finalFirstName = firstName as string;
        } else if (directName) {
            const nameParts = (directName as string).trim().split(' ');
            finalFirstName = nameParts[0] || '';
            finalLastName = nameParts.slice(1).join(' ') || '';
        }

        // Handle last name
        if (lastName) {
            finalLastName = lastName as string;
        }

        // Add prefill parameters
        if (finalFirstName) {
            url.searchParams.set('firstname', finalFirstName);
        }

        if (finalLastName) {
            url.searchParams.set('lastname', finalLastName);
        }

        if (email) {
            url.searchParams.set('email', email as string);
        }

        if (phone) {
            url.searchParams.set('phone', phone as string);
        }

        if (company) {
            url.searchParams.set('company', company as string);
        }

        // Add UTM parameters for tracking
        const { utm_campaign, utm_source, utm_medium } = trackingProperties;

        if (utm_source) {
            url.searchParams.set('utm_source', utm_source as string);
        }

        if (utm_medium) {
            url.searchParams.set('utm_medium', utm_medium as string);
        }

        if (utm_campaign) {
            url.searchParams.set('utm_campaign', utm_campaign as string);
        }

        return url.toString();
    },
};
